/**
 * Integrytas Group - Document Management System (DMS)
 * Component: DocumentManager  (REUSABLE)
 *
 * Drop-in document panel for ANY screen. Give it a polymorphic target
 * (entityName + recordId) and it provides upload, versioned history, view,
 * revert, and a notes thread - all through the DMS base services. Future
 * modules embed this directly:
 *
 *   <DocumentManager targetEntityName="fin_invoice" targetRecordId={invoiceId} session={session} />
 *
 * Two upload conveniences:
 *  • every upload takes an optional TITLE and DESCRIPTION alongside the file;
 *  • `pending` mode lets a CREATE form collect documents BEFORE the record
 *    exists — files are staged in memory (nothing is written to storage), and
 *    the parent calls the imperative `flush(recordId)` (via a ref) once it has
 *    saved the record. If the create is cancelled the staged files are simply
 *    discarded — nothing to clean up, because nothing was uploaded.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getForensicActorId } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import {
  DmsService,
  type DmsDocument,
  type DmsNote,
} from '../../services/dms/DmsService';
import { EmptyState } from '../common/EmptyState';

export interface DocumentManagerProps {
  targetEntityName: string;
  targetRecordId: string;
  session: AuthSession;
  /** Optional: hide the notes panel (e.g. embedded in a tight drawer). */
  showNotes?: boolean;
  /**
   * CREATE-form mode: the target record does not exist yet, so uploads are
   * staged in memory instead of written. The parent flushes them to the new
   * record id (via the ref handle) after it saves.
   */
  pending?: boolean;
}

/** Imperative handle for the CREATE-form (`pending`) flow. */
export interface DocumentManagerHandle {
  /** Upload every staged document against the freshly-created record. */
  flush: (recordId: string) => Promise<void>;
  /** Whether any documents are staged and waiting to be flushed. */
  hasStaged: () => boolean;
}

interface StagedDoc { key: string; file: File; title: string; description: string }

export const DocumentManager = forwardRef<DocumentManagerHandle, DocumentManagerProps>(function DocumentManager(
  { targetEntityName, targetRecordId, session, showNotes = true, pending = false },
  ref,
) {
  const { t } = useTranslation();
  const actorId = getForensicActorId(session);
  const [docs, setDocs] = useState<DmsDocument[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<DmsDocument | null>(null);
  const verFileRef = useRef<HTMLInputElement>(null);

  // Upload-form fields (shared by the immediate and staged paths).
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const newFileRef = useRef<HTMLInputElement>(null);

  // Staged uploads, only used in `pending` mode.
  const [staged, setStaged] = useState<StagedDoc[]>([]);

  const hasTarget = Boolean(targetEntityName && targetRecordId);

  const reload = useCallback(async () => {
    if (pending || !hasTarget) { setDocs([]); return; }
    setBusy(true); setError(null);
    try {
      setDocs(await DmsService.listLatestForTarget(targetEntityName, targetRecordId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [pending, hasTarget, targetEntityName, targetRecordId]);

  useEffect(() => { void reload(); }, [reload]);

  const clearForm = () => {
    setNewFile(null); setNewTitle(''); setNewDesc('');
    if (newFileRef.current) newFileRef.current.value = '';
  };

  // Add: in pending mode stage the file; otherwise upload it immediately.
  const onAdd = async () => {
    if (!newFile) return;
    if (pending) {
      setStaged(xs => [...xs, { key: `${Date.now()}-${xs.length}`, file: newFile, title: newTitle.trim(), description: newDesc.trim() }]);
      clearForm();
      return;
    }
    setBusy(true); setError(null);
    try {
      await DmsService.uploadNewDocument(
        { targetEntityName, targetRecordId, actorUserId: actorId },
        newFile,
        { title: newTitle.trim() || null, description: newDesc.trim() || null },
      );
      clearForm();
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const removeStaged = (key: string) => setStaged(xs => xs.filter(s => s.key !== key));

  // Imperative handle for the CREATE flow.
  const flush = useCallback(async (recordId: string) => {
    if (!recordId || staged.length === 0) return;
    for (const s of staged) {
      await DmsService.uploadNewDocument(
        { targetEntityName, targetRecordId: recordId, actorUserId: actorId },
        s.file,
        { title: s.title || null, description: s.description || null },
      );
    }
    setStaged([]);
  }, [staged, targetEntityName, actorId]);

  useImperativeHandle(ref, () => ({ flush, hasStaged: () => staged.length > 0 }), [flush, staged.length]);

  const [versions, setVersions] = useState<DmsDocument[]>([]);
  const openHistory = async (doc: DmsDocument) => {
    setExpanded(doc); setError(null);
    try {
      setVersions(await DmsService.getVersions(doc.document_id));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onUploadVersion = async (documentId: number, file: File) => {
    setBusy(true); setError(null);
    try {
      await DmsService.uploadNewVersion(documentId, file, actorId);
      await reload();
      if (expanded) await openHistory(expanded);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (verFileRef.current) verFileRef.current.value = '';
    }
  };

  const onView = async (doc: DmsDocument) => {
    try {
      const url = await DmsService.getSignedUrl(doc.storage_path);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onRevert = async (versionDocId: number) => {
    if (!confirm(t('common.dms.revert_confirm', 'Create a new latest version from this prior version?'))) return;
    setBusy(true); setError(null);
    try {
      await DmsService.revertToVersion(versionDocId);
      await reload();
      if (expanded) await openHistory(expanded);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'h-7 rounded border border-csm-border px-2 text-csm-sm';

  return (
    <div className="flex flex-col gap-3">
      {/* Upload form — file + optional title/description. In pending mode the
          Add button stages; otherwise it uploads straight away. */}
      <div className="flex flex-wrap items-end gap-2 border-b border-csm-border pb-2">
        <span className="w-full text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('common.dms.documents', 'Documents')}</span>
        <label className="flex flex-col text-csm-2xs uppercase tracking-wider text-csm-ink-soft">
          {t('common.dms.file', 'File')}
          <input ref={newFileRef} type="file" disabled={busy}
            onChange={e => setNewFile(e.target.files?.[0] ?? null)} className="mt-0.5 text-csm-xs" />
        </label>
        <label className="flex flex-col text-csm-2xs uppercase tracking-wider text-csm-ink-soft">
          {t('common.dms.doc_title', 'Title')}
          <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder={t('common.dms.doc_title_ph', 'optional')} className={`${inputCls} mt-0.5 w-44`} />
        </label>
        <label className="flex flex-col text-csm-2xs uppercase tracking-wider text-csm-ink-soft">
          {t('common.dms.doc_description', 'Description')}
          <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder={t('common.dms.doc_description_ph', 'optional')} className={`${inputCls} mt-0.5 w-60`} />
        </label>
        <button onClick={() => void onAdd()} disabled={busy || !newFile}
          className="h-7 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
          {pending ? t('common.dms.stage', 'Add') : t('common.dms.upload', 'Upload')}
        </button>
        {!pending && (
          <button onClick={() => void reload()} disabled={!hasTarget}
            className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('common.dms.refresh', 'Refresh')}</button>
        )}
        {busy && <span className="text-csm-xs text-csm-watermark">{t('common.dms.working', 'Working…')}</span>}
        {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
      </div>

      {/* Pending mode: staged files waiting for the record to be saved. */}
      {pending && (
        staged.length === 0 ? (
          <div className="px-1 py-2 text-csm-sm italic text-csm-watermark">
            {t('common.dms.pending_hint', 'Documents added here are attached when you save the record.')}
          </div>
        ) : (
          <table className="min-w-full border-collapse text-csm-md">
            <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
              <tr>
                <th className="border-b border-csm-border px-2 py-1 text-left">{t('common.dms.name', 'Name')}</th>
                <th className="border-b border-csm-border px-2 py-1 text-left">{t('common.dms.doc_description', 'Description')}</th>
                <th className="border-b border-csm-border px-2 py-1 text-right">{t('common.dms.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {staged.map(s => (
                <tr key={s.key} className="hover:bg-csm-surface-alt">
                  <td className="border-b border-csm-border-soft px-2 py-1">{s.title || s.file.name}</td>
                  <td className="border-b border-csm-border-soft px-2 py-1 text-csm-ink-soft">{s.description || '—'}</td>
                  <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                    <button onClick={() => removeStaged(s.key)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-danger-soft">{t('common.remove', 'Remove')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {!pending && (
        <table className="min-w-full border-collapse text-csm-md">
          <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
            <tr>
              <th className="border-b border-csm-border px-2 py-1 text-left">{t('common.dms.name', 'Name')}</th>
              <th className="border-b border-csm-border px-2 py-1 text-left">{t('common.dms.doc_description', 'Description')}</th>
              <th className="border-b border-csm-border px-2 py-1 text-left">{t('common.dms.type', 'Type')}</th>
              <th className="border-b border-csm-border px-2 py-1 text-right">{t('common.dms.ver', 'Ver')}</th>
              <th className="border-b border-csm-border px-2 py-1 text-right">{t('common.dms.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.document_id} className="hover:bg-csm-surface-alt">
                <td className="border-b border-csm-border-soft px-2 py-1">{d.title || d.original_name}</td>
                <td className="border-b border-csm-border-soft px-2 py-1 text-csm-ink-soft">{d.description ?? '—'}</td>
                <td className="border-b border-csm-border-soft px-2 py-1 text-csm-ink-soft">{d.file_type ?? '—'}</td>
                <td className="border-b border-csm-border-soft px-2 py-1 text-right font-mono">v{d.version_number}</td>
                <td className="border-b border-csm-border-soft px-2 py-1 text-right whitespace-nowrap">
                  <button onClick={() => void onView(d)} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.dms.view', 'View')}</button>
                  <button onClick={() => void openHistory(d)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.dms.history', 'History')}</button>
                </td>
              </tr>
            ))}
            {docs.length === 0 && hasTarget && !busy && (
              <tr>
                <td colSpan={5} className="py-8 text-center">
                  <EmptyState title={t('common.dms.no_documents', 'No documents attached.')} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {expanded && (
        <div className="rounded border border-csm-border bg-white p-3">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-csm-sm font-semibold text-csm-ink-soft">{t('common.dms.version_history', 'Version history — {{name}}', { name: expanded.title || expanded.original_name })}</span>
            <input
              ref={verFileRef} type="file" disabled={busy}
              onChange={e => { const f = e.target.files?.[0]; if (f) void onUploadVersion(expanded.document_id, f); }}
              className="ml-auto text-csm-xs"
            />
            <span className="text-csm-2xs text-csm-watermark">{t('common.dms.upload_new_version', '↑ upload new version')}</span>
            <button onClick={() => setExpanded(null)} className="text-csm-muted hover:text-csm-ink" aria-label={t('common.close', 'Close')}>x</button>
          </div>
          <table className="min-w-full border-collapse text-csm-sm">
            <tbody>
              {versions.map(v => (
                <tr key={v.document_id} className="hover:bg-csm-surface-alt">
                  <td className="border-b border-csm-border-soft px-2 py-1 font-mono">v{v.version_number}{v.is_latest === 'Y' ? ` ${t('common.dms.latest', '(latest)')}` : ''}</td>
                  <td className="border-b border-csm-border-soft px-2 py-1 text-csm-ink-soft">{new Date(v.upload_timestamp).toLocaleString()}</td>
                  <td className="border-b border-csm-border-soft px-2 py-1 text-right whitespace-nowrap">
                    <button onClick={() => void onView(v)} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.dms.view', 'View')}</button>
                    {v.is_latest !== 'Y' && (
                      <button onClick={() => void onRevert(v.document_id)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.dms.revert_to_this', 'Revert to this')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {showNotes && <NotesThread documentId={expanded.root_document_id} session={session} />}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Notes thread (polymorphic, attached to the document family)
// ---------------------------------------------------------------------------

function NotesThread({ documentId, session }: { documentId: number; session: AuthSession }) {
  const { t } = useTranslation();
  const actorId = getForensicActorId(session);
  const [notes, setNotes] = useState<DmsNote[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setNotes(await DmsService.getNotes(documentId));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [documentId]);

  useEffect(() => { void reload(); }, [reload]);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true); setError(null);
    try {
      await DmsService.addNote(documentId, text.trim(), null, actorId);
      setText('');
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-csm-border pt-2">
      <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('common.dms.notes', 'Notes')}</div>
      <div className="mb-2 flex flex-col gap-1">
        {notes.map(n => (
          <div key={n.note_id} className="rounded bg-csm-surface-alt px-2 py-1 text-csm-sm">
            <span className="text-csm-2xs text-csm-watermark">{new Date(n.created_at).toLocaleString()} · {t('common.dms.user', 'user')} {n.created_by_user_id ?? '—'}</span>
            <div className="text-csm-ink">{n.note_text}</div>
          </div>
        ))}
        {notes.length === 0 && <span className="text-csm-xs italic text-csm-watermark">{t('common.dms.no_notes', 'No notes yet.')}</span>}
      </div>
      <div className="flex gap-2">
        <textarea
          rows={2} value={text} onChange={e => setText(e.target.value)}
          placeholder={t('common.dms.add_note', 'Add a note…')}
          className="flex-1 rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-csm-watermark focus:outline-none"
        />
        <button onClick={() => void submit()} disabled={busy || !text.trim()}
                className="self-end rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{t('common.dms.post', 'Post')}</button>
      </div>
      {error && <div className="mt-1 text-csm-xs text-csm-danger">{error}</div>}
    </div>
  );
}
